package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StockHistoryDetailsScreen(
    supplierId: String,
    countId: String,
    timestamp: String,
    onBack: () -> Unit
) {
    val userRole = LocalUserRole.current
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    
    var details by remember { mutableStateOf<List<StockCountItem>>(emptyList()) }
    var itemNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var itemsMap by remember { mutableStateOf<Map<String, Item>>(emptyMap()) }
    var isLoading by remember { mutableStateOf(true) }

    LaunchedEffect(countId) {
        isLoading = true
        val itemsResult = repository.getItems(supplierId)
        val detailsResult = repository.getStockCountDetails(countId)
        
        if (itemsResult.isSuccess) {
            val list = itemsResult.getOrThrow()
            itemsMap = list.associateBy { it.id }
            itemNames = list.associate { it.id to it.name }
        }
        
        if (detailsResult.isSuccess) {
            details = detailsResult.getOrThrow()
        }
        isLoading = false
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = {
                        Text(
                            DateTimeUtils.formatTitleDate(timestamp).ifBlank { DateTimeUtils.formatCountTimestamp(timestamp) },
                            style = MaterialTheme.typography.titleMedium
                        )
                    },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (!isLoading) {
                    Column(modifier = Modifier.fillMaxSize()) {
                        // Header
                        Row(
                            modifier = Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.primary).padding(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text("Item Name", modifier = Modifier.weight(2f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold)
                            Text("Counted", modifier = Modifier.weight(1f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold)
                        }

                        LazyColumn(modifier = Modifier.weight(1f)) {
                            items(details) { item ->
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(8.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(
                                        text = itemNames[item.itemId] ?: item.name ?: "Item #${item.itemId}", 
                                        modifier = Modifier.weight(2f)
                                    )
                                    Text(formatStockAmount(itemsMap[item.itemId], item.countedRealAmount), modifier = Modifier.weight(1f), fontWeight = FontWeight.Bold)
                                }
                                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                            }
                        }

                        if (userRole == UserRole.ADMIN) {
                            val totalValuation = remember(details, itemsMap) {
                                var total = 0.0
                                details.forEach { detail ->
                                    val item = itemsMap[detail.itemId]
                                    if (item != null) {
                                        total += calculateItemCost(item, detail.countedRealAmount)
                                    }
                                }
                                total
                            }
                            Surface(
                                color = MaterialTheme.colorScheme.primaryContainer,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Row(
                                    modifier = Modifier.padding(16.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Text("TOTAL STOCK VALUATION", fontWeight = FontWeight.Bold)
                                    Text("£${formatDecimal(totalValuation)}", fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                                }
                            }
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }
    }
}
